import { useEffect, useState } from 'react';

import { useAuthStatus } from './useAuth.js';

import type { z } from 'zod';

// One SSE connection per open tab, fed by a single server-side poll per signed-in user —
// Spotify only ever sees one request per interval, however many tabs are open.
// Events that fail schema validation are dropped, not thrown: a stream is a background
// nicety, so one bad frame shouldn't take the page down.
export const useSpotifyStream = <T>(url: string, schema: z.ZodType<T>): T | undefined => {
  const status = useAuthStatus();
  const isAuthenticated = status.data?.authenticated === true;
  const [value, setValue] = useState<T | undefined>(undefined);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const source = new EventSource(url, { withCredentials: true });
    source.addEventListener('message', (event: MessageEvent<string>): void => {
      const parsed = schema.safeParse(JSON.parse(event.data));
      if (parsed.success) {
        setValue(parsed.data);
      }
    });

    return (): void => {
      source.close();
    };
  }, [isAuthenticated, url, schema]);

  return isAuthenticated ? value : undefined;
};
